import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { orderBy, noop, capitalize } from 'lodash-es';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import Search from 'sushiweb/Icons/all/Search';
import Tooltip from 'sushiweb/Tooltip/Tooltip';
import Download from 'sushiweb/Icons/all/Download';
import {
  exportCSV,
  formatDateTime,
  formatDateinDDMMMYYY,
  formatQuestionsColumn,
  getCurrentDateTime,
  getTimeFromDate,
  getValueForKey,
  includesOption,
  indexOfOption,
  searchWithName,
  formatDateSorting,
} from '../../../helpers';
import {
  SearchContainerV2,
  SearchInput,
  StatusFilterAndExportCSVContainer,
  TableContainer,
} from '../../common/styles';
import DataTables from '../../common/components/DataTables/DataTables';
import { employeeConsumptionsHeadColumns } from '../../common/components/DataTables/components/headColumns';
import ErrorPage from '../../common/components/ErrorPage';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  currentFlowTypeEnum,
  orderServiceTypeEnum,
  orderStatusEnum,
  statusLabels,
} from '../../common/components/constants';
import StatusFilter from '../../common/components/StatusFilter';

const EmployeeConsumptions = ({
  currentFlow,
  employeeData,
  employeeOrdersData,
  fetchingOrders,
  fetchedOrders,
  getEmployeeConsumptions,
}) => {
  const [consumptionHeads, setConsumptionHeads] = useState([]);
  const [downloadQuestionsHeaders, setDownloadQuestionsHeader] = useState([]);
  const { emailHash } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const keyFields = ['order_id', 'date_time'];
  const [search, onInputChange] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const consumptionDetails = employeeOrdersData;
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [1],
      label: 'ORDER_PLACED_PAID',
      disabled: true,
    },
    {
      value: [3],
      label: 'ORDER_REFUNDED',
      disabled: true,
    },
    {
      value: [4],
      label: 'ORDER_PARTIALLY_REFUNDED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  const createHeadColumns = orders => {
    const questionsMap = {};
    const questionsHeader = [];
    const downloadCSVQuestionsHeader = [];
    orders.forEach(order => {
      const userDefinedInputs = order?.orders_meta_data?.user_defined_inputs;
      if (userDefinedInputs) {
        userDefinedInputs.forEach(val => {
          if (!(val.key in questionsMap)) {
            questionsHeader.push({
              name: formatQuestionsColumn(val.key),
              selector: row =>
                getValueForKey(
                  row?.orders_meta_data?.user_defined_inputs,
                  val.key,
                ),
              sortable: true,
              grow: 1,
              cell: row =>
                getValueForKey(
                  row?.orders_meta_data?.user_defined_inputs,
                  val.key,
                ),
            });
            downloadCSVQuestionsHeader.push({ key: val.key, value: val.key });
            questionsMap[val.key] = 1;
          }
        });
      }
    });
    setConsumptionHeads(questionsHeader);
    setDownloadQuestionsHeader(downloadCSVQuestionsHeader);
  };
  useEffect(() => {
    getEmployeeConsumptions(emailHash);
  }, [currentFlow]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!fetchingOrders && fetchedOrders) {
      if (consumptionDetails) {
        consumptionDetails.sort(
          (a, b) =>
            formatDateSorting(b.created_on) - formatDateSorting(a.created_on),
        );
        if (consumptionDetails.length === 0) {
          setDisabledSearch(true);
        }
        if (consumptionDetails.length > 0) {
          createHeadColumns(consumptionDetails);
        }
        setTableData(consumptionDetails);
        setSearchResults(consumptionDetails);
        setCurrentData(consumptionDetails.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingOrders]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingOrders) {
        const consumptionDetailsWithDate = [];
        consumptionDetails.forEach(data => {
          const consData = { ...data };
          consData.date_time = formatDateTime(data.created_on);
          consumptionDetailsWithDate.push(consData);
        });
        const arr = searchWithName(
          consumptionDetailsWithDate,
          search,
          keyFields,
        );
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);
    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingOrders]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getEmployeeConsumptions(emailHash);
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };

  const clearFilters = () => {
    setCurrentFilters([]);
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'order_id', value: 'Order ID' },
      { key: 'date', value: 'Date' },
      { key: 'time', value: 'Time' },
      { key: 'service_type', value: 'Service Type' },
      { key: 'restaurant', value: 'Restaurant' },
      { key: 'city', value: 'City' },
      { key: 'state', value: 'State' },
      { key: 'address', value: 'Address' },
      { key: 'limit_order_amount', value: 'Amount paid by limit' },
      { key: 'refund_amount', value: 'Refund amount' },
      { key: 'total_order_amount', value: 'Total order value' },
      { key: 'paid_using_limit', value: 'Paid by the corporate' },
      { key: 'paid_by_customer', value: 'Paid by customer' },
      { key: 'item_value', value: 'Item value' },
      { key: 'delivery_charge', value: 'Delivery charge' },
      { key: 'tax', value: 'Taxes' },
      { key: 'platform_fee', value: 'Platform fee' },
      { key: 'discount', value: 'Discount' },
      { key: 'misc_charges', value: 'Other Charge' },
      { key: 'priority_fee', value: 'Priority Fee' },
      { key: 'rider_tip', value: 'Rider Tip' },
      { key: 'fi_donation', value: 'Donation' },
      { key: 'pkg_fee', value: 'Packaging Charges' },
      { key: 'status', value: 'Status' },
    ];

    const statusLabelledData = [];
    searchResults.forEach(data => {
      let labelledData = { ...data };
      labelledData.limit_order_amount = data.amount;
      labelledData.restaurant = data?.orders_meta_data?.restaurant_name.replace(
        /,/g,
        '',
      );
      labelledData.total_order_amount =
        data?.orders_meta_data?.total_order_value;
      labelledData.paid_using_limit =
        data?.orders_meta_data?.corporate_paid_amount;
      labelledData.paid_by_customer =
        data?.orders_meta_data?.customer_paid_amount;
      labelledData.item_value = data?.orders_meta_data?.item_amount;
      labelledData.delivery_charge = data?.orders_meta_data?.delivery_charge;
      labelledData.tax = data?.orders_meta_data?.taxes;
      labelledData.platform_fee = data?.orders_meta_data?.platform_fee;
      labelledData.state = data?.orders_meta_data?.state;
      labelledData.city = data?.orders_meta_data?.city;
      labelledData.address = data?.orders_meta_data?.address.replace(/,/g, '');
      labelledData.pkg_fee = data?.orders_meta_data?.pkg_fee;
      labelledData.fi_donation = data?.orders_meta_data?.fi_donation;
      labelledData.rider_tip = data?.orders_meta_data?.rider_tip;
      labelledData.priority_fee = data?.orders_meta_data?.priority_fee;
      labelledData.discount = data?.orders_meta_data?.total_discounts;
      labelledData.misc_charges = data?.orders_meta_data?.msc_charges;
      labelledData.date = formatDateinDDMMMYYY(data.created_on);
      labelledData.time = getTimeFromDate(data.created_on);
      labelledData.service_type =
        statusLabels[
          orderServiceTypeEnum[(data?.orders_meta_data?.service_type)]
        ];
      labelledData.status = capitalize(
        statusLabels[orderStatusEnum[data.status]],
      );
      const userDefinedInputs = data?.orders_meta_data?.user_defined_inputs;
      let questionsData = {};
      if (userDefinedInputs)
        userDefinedInputs.forEach(val => {
          const newOues = {
            [val.key]: val.value,
          };
          questionsData = { ...questionsData, ...newOues };
        });
      labelledData = { ...labelledData, ...questionsData };
      statusLabelledData.push(labelledData);
    });

    exportCSV(
      statusLabelledData,
      `Employee Consumption details - ${
        employeeData.email
      } - ${getCurrentDateTime()}`,
      [...headColumns, ...downloadQuestionsHeaders],
    );
  };
  const downloadCSVStyle = {
    padding: '15px 20px 10px 0px',
    cursor: 'pointer',
  };

  return (
    <>
      <SearchContainerV2>
        <SearchInput
          leftIcon={<Search />}
          innerWidth='100%'
          value={search}
          onChange={onInputChange}
          onFocus={() => setIsSearchClicked(true)}
          onBlur={() => setIsSearchClicked(false)}
          label={
            isSearchClicked || disabledSearch ? (
              'Search by order ID or date'
            ) : (
              <span style={{ fontSize: '16px', fontWeight: 300 }}>
                {' '}
                Search by order ID or date{' '}
              </span>
            )
          }
          disabled={disabledSearch}
        />
        <StatusFilterAndExportCSVContainer>
          <StatusFilter
            isDisabled={disabledSearch}
            clearFilters={clearFilters}
            currentFilters={currentFilters}
            options={filterStatusOptions}
            handleSelect={handleSelect}
          />
          {disabledSearch ? (
            <Download color='grey' style={{ padding: '10px 20px 20px 0px' }} />
          ) : (
            <Tooltip
              position='top'
              label='Download the consumption details'
              appearance='light'
              size='big'
              style={{
                fontSize: '12px',
                opacity: 0.8,
                marginRight: '10px',
                marginLeft: '-30px',
                padding: '10px',
              }}
            >
              <Download
                color='grey'
                onClick={exportSearchResultCSV}
                style={downloadCSVStyle}
              />
            </Tooltip>
          )}
        </StatusFilterAndExportCSVContainer>
      </SearchContainerV2>
      <TableContainer>
        {!fetchedOrders ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <DataTables
            data={currentData}
            columns={[...employeeConsumptionsHeadColumns, ...consumptionHeads]}
            loading={fetchingOrders || loading}
            pagination
            handlePageChange={handlePageChange}
            totalRows={searchResults ? searchResults.length : 0}
            onSort={handleSort}
            emptyStateTitle='No consumption details for employee'
          />
        )}
      </TableContainer>
    </>
  );
};

EmployeeConsumptions.propTypes = {
  employeeData: PropTypes.objectOf(PropTypes.any),
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  employeeOrdersData: PropTypes.objectOf(PropTypes.any),
  getEmployeeConsumptions: PropTypes.func,
  fetchingOrders: PropTypes.bool,
  fetchedOrders: PropTypes.bool,
};

EmployeeConsumptions.defaultProps = {
  employeeData: noop,
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  employeeOrdersData: {},
  getEmployeeConsumptions: noop,
  fetchingOrders: true,
  fetchedOrders: true,
};

export default EmployeeConsumptions;
